import axios from 'axios';
import { API_ENDPOINTS, axiosConfig, apiRequest } from '../config/api';

// Create axios instance with default config
const apiClient = axios.create(axiosConfig);

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      const currentPath = window.location.pathname;
      // Only redirect if not already on login page to prevent infinite loops
      if (currentPath !== '/login' && !currentPath.startsWith('/login')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Generic CRUD operations
export const crudService = {
  // Get all records
  getAll: async (endpoint, params) => {
    const response = await apiClient.get(endpoint, { params });
    return response.data;
  },

  // Get single record by ID
  getById: async (endpoint, id) => {
    const response = await apiClient.get(`${endpoint}/${id}`);
    return response.data;
  },

  // Create new record
  create: async (endpoint, data) => {
    const response = await apiClient.post(endpoint, data);
    return response.data;
  },

  // Update record by ID
  update: async (endpoint, id, data) => {
    const response = await apiClient.put(`${endpoint}/${id}`, data);
    return response.data;
  },

  // Delete record by ID
  delete: async (endpoint, id) => {
    const response = await apiClient.delete(`${endpoint}/${id}`);
    return response.data;
  },
};

// Specific service functions for each entity
export const userService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.USERS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.USERS, id),
  create: (data) => crudService.create(API_ENDPOINTS.USERS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.USERS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.USERS, id),
};

export const roleService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.ROLES),
  getById: (id) => crudService.getById(API_ENDPOINTS.ROLES, id),
  create: (data) => crudService.create(API_ENDPOINTS.ROLES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.ROLES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.ROLES, id),
};


export const teacherService = {
  getAll: (params = {}) => crudService.getAll(API_ENDPOINTS.USERS, { ...params, role_name: 'Teacher' }), // Filter by Teacher role, merge with additional params
  getById: (id) => crudService.getById(API_ENDPOINTS.USERS, id),
  create: (data) => crudService.create(API_ENDPOINTS.USERS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.USERS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.USERS, id),
};

export const parentService = {
  getAll: (params = {}) => crudService.getAll(API_ENDPOINTS.USERS, { ...params, role_name: 'Parent' }), // Filter by Parent role, merge with additional params
  getById: (id) => crudService.getById(API_ENDPOINTS.USERS, id),
  create: (data) => crudService.create(API_ENDPOINTS.USERS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.USERS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.USERS, id),
};

export const studentService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.STUDENTS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.STUDENTS, id),
  getDetails: (studentId) => crudService.getAll(API_ENDPOINTS.STUDENT_DETAILS, { student_id: studentId }),
  create: (data) => crudService.create(API_ENDPOINTS.STUDENTS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.STUDENTS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.STUDENTS, id),
};

export const classService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.CLASSES),
  getById: (id) => crudService.getById(API_ENDPOINTS.CLASSES, id),
  create: (data) => crudService.create(API_ENDPOINTS.CLASSES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.CLASSES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.CLASSES, id),
  checkRollOrder: (classId) => apiClient.get(API_ENDPOINTS.CLASSES_REORDER_ROLL, { params: { class_id: classId } }).then(res => res.data),
  reorderRollNumbers: (classId) => apiClient.post(API_ENDPOINTS.CLASSES_REORDER_ROLL, { class_id: classId }).then(res => res.data),
};

export const subjectService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.SUBJECTS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.SUBJECTS, id),
  create: (data) => crudService.create(API_ENDPOINTS.SUBJECTS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.SUBJECTS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.SUBJECTS, id),
};

export const classSubjectService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.CLASS_SUBJECTS),
  getById: (id) => crudService.getById(API_ENDPOINTS.CLASS_SUBJECTS, id),
  create: (data) => crudService.create(API_ENDPOINTS.CLASS_SUBJECTS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.CLASS_SUBJECTS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.CLASS_SUBJECTS, id),
};

export const examService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.EXAMS),
  getById: (id) => crudService.getById(API_ENDPOINTS.EXAMS, id),
  create: (data) => crudService.create(API_ENDPOINTS.EXAMS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.EXAMS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.EXAMS, id),
};

export const markService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.MARKS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.MARKS, id),
  create: (data) => crudService.create(API_ENDPOINTS.MARKS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.MARKS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.MARKS, id),
};

export const attendanceService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.ATTENDANCE, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.ATTENDANCE, id),
  create: (data) => crudService.create(API_ENDPOINTS.ATTENDANCE, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.ATTENDANCE, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.ATTENDANCE, id),
};

export const notificationService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.NOTIFICATIONS),
  getById: (id) => crudService.getById(API_ENDPOINTS.NOTIFICATIONS, id),
  create: (data) => crudService.create(API_ENDPOINTS.NOTIFICATIONS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.NOTIFICATIONS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.NOTIFICATIONS, id),
};

export const notificationUserService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.NOTIFICATION_USERS),
  getById: (id) => crudService.getById(API_ENDPOINTS.NOTIFICATION_USERS, id),
  create: (data) => crudService.create(API_ENDPOINTS.NOTIFICATION_USERS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.NOTIFICATION_USERS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.NOTIFICATION_USERS, id),
};

export const leaveAllocationService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.LEAVE_ALLOCATION, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.LEAVE_ALLOCATION, id),
  create: (data) => crudService.create(API_ENDPOINTS.LEAVE_ALLOCATION, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.LEAVE_ALLOCATION, id, data),
  delete: async (id) => {
    const response = await apiClient.delete(API_ENDPOINTS.LEAVE_ALLOCATION, { data: { id } });
    return response.data;
  },
};

export const leaveRequestService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.LEAVE_REQUESTS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.LEAVE_REQUESTS, id),
  create: (data) => crudService.create(API_ENDPOINTS.LEAVE_REQUESTS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.LEAVE_REQUESTS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.LEAVE_REQUESTS, id),
};

export const dashboardService = {
  get: () => crudService.getAll(API_ENDPOINTS.DASHBOARD),
};

export const teacherDashboardService = {
  get: () => crudService.getAll(API_ENDPOINTS.TEACHER_DASHBOARD),
};

export const activityService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.ACTIVITIES, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.ACTIVITIES, id),
  create: (data) => crudService.create(API_ENDPOINTS.ACTIVITIES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.ACTIVITIES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.ACTIVITIES, id),
};

export const announcementService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.ANNOUNCEMENTS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.ANNOUNCEMENTS, id),
  create: (data) => crudService.create(API_ENDPOINTS.ANNOUNCEMENTS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.ANNOUNCEMENTS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.ANNOUNCEMENTS, id),
};

export const appConfigService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.APPCONFIG, params),
  getByKey: (key) => crudService.getAll(API_ENDPOINTS.APPCONFIG, { config_key: key }),
  getById: (id) => crudService.getById(API_ENDPOINTS.APPCONFIG, id),
  create: (data) => crudService.create(API_ENDPOINTS.APPCONFIG, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.APPCONFIG, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.APPCONFIG, id),
  getPublicTheme: async () => {
    const response = await apiRequest(API_ENDPOINTS.APPCONFIG_PUBLIC_THEME);
    return response;
  },
};

export const uploadService = {
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('token');
    const response = await apiClient.post(API_ENDPOINTS.UPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  },
};

export const periodService = {
  getAll: () => crudService.getAll(API_ENDPOINTS.PERIODS),
  getById: (id) => crudService.getById(API_ENDPOINTS.PERIODS, id),
  create: (data) => crudService.create(API_ENDPOINTS.PERIODS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.PERIODS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.PERIODS, id),
};

export const timetableService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.TIMETABLE, params),
  saveDay: (data) => apiClient.post(API_ENDPOINTS.TIMETABLE, data).then(res => res.data),
  delete: (id) => crudService.delete(API_ENDPOINTS.TIMETABLE, id),
};

export const feeCategoryService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.FEE_CATEGORIES, params),
  create: (data) => crudService.create(API_ENDPOINTS.FEE_CATEGORIES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.FEE_CATEGORIES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.FEE_CATEGORIES, id),
};

export const feeClassFeeService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.FEE_CLASS_FEES, params),
  create: (data) => crudService.create(API_ENDPOINTS.FEE_CLASS_FEES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.FEE_CLASS_FEES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.FEE_CLASS_FEES, id),
};

export const feeStudentFeeService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.FEE_STUDENT_FEES, params),
  create: (data) => crudService.create(API_ENDPOINTS.FEE_STUDENT_FEES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.FEE_STUDENT_FEES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.FEE_STUDENT_FEES, id),
};

export const feeInvoiceService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.FEE_INVOICES, params),
  create: (data) => crudService.create(API_ENDPOINTS.FEE_INVOICES, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.FEE_INVOICES, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.FEE_INVOICES, id),
  getStudentSummary: (params) => crudService.getAll(API_ENDPOINTS.FEE_INVOICE_STUDENT, params),
};

export const feePaymentService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.FEE_PAYMENTS, params),
  create: (data) => crudService.create(API_ENDPOINTS.FEE_PAYMENTS, data),
  generateBill: (data) =>
    apiClient.post(API_ENDPOINTS.FEE_PAYMENT_BILL, data, {
      responseType: 'blob',
    }),
};

export const promotionService = {
  getSummary: () =>
    apiRequest(API_ENDPOINTS.PROMOTION_SUMMARY, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }),
  execute: (payload) =>
    apiRequest(API_ENDPOINTS.PROMOTION_EXECUTE, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }),
};

export const contactService = {
  submit: async (data) => {
    const response = await apiClient.post(API_ENDPOINTS.CONTACT, data);
    return response.data;
  },
};

export const emailService = {
  sendTest: async (payload) => {
    const response = await apiClient.post(API_ENDPOINTS.EMAIL_TEST, payload);
    return response.data;
  },
};

export const sessionService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.SESSIONS, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.SESSIONS, id),
  create: (data) => crudService.create(API_ENDPOINTS.SESSIONS, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.SESSIONS, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.SESSIONS, id),
  getActive: () => apiClient.get(`${API_ENDPOINTS.SESSIONS}?is_active=true`).then(res => res.data),
};

export const activityLogService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.ACTIVITY_LOGS, params),
  create: (data) => crudService.create(API_ENDPOINTS.ACTIVITY_LOGS, data),
};

export const feedbackService = {
  getAll: (params) => crudService.getAll(API_ENDPOINTS.FEEDBACK, params),
  getById: (id) => crudService.getById(API_ENDPOINTS.FEEDBACK, id),
  create: (data) => crudService.create(API_ENDPOINTS.FEEDBACK, data),
  update: (id, data) => crudService.update(API_ENDPOINTS.FEEDBACK, id, data),
  delete: (id) => crudService.delete(API_ENDPOINTS.FEEDBACK, id),
};

export default apiClient;
